import { cloneElement, createElement, Fragment, isValidElement } from 'react';
import type { ReactNode } from 'react';
import { escapeRegExp } from 'lodash';

const highlightText = (text: string, matcher: RegExp): ReactNode => {
  const parts = text.split(matcher);
  if (parts.length === 1) return text;

  return createElement('span', null, ...parts.map((part, index) => (index % 2 ? <b>{part}</b> : part)));
};

const highlightList = (nodes: ReactNode[], matcher: RegExp): ReactNode[] => {
  const highlightedNodes = nodes.map(node => highlightNode(node, matcher));
  return highlightedNodes.some((node, index) => node !== nodes[index]) ? highlightedNodes : nodes;
};

const highlightNode = (node: ReactNode, matcher: RegExp): ReactNode => {
  if (typeof node === 'string' || typeof node === 'number') return highlightText(String(node), matcher);
  if (Array.isArray(node)) {
    const highlightedNodes = highlightList(node, matcher);
    return highlightedNodes === node ? node : createElement(Fragment, null, ...highlightedNodes);
  }
  if (!isValidElement<{ children?: ReactNode }>(node)) return node;

  const { children } = node.props;
  if (children === undefined || children === null || typeof children === 'function') return node;

  if (Array.isArray(children)) {
    const highlightedChildren = highlightList(children, matcher);
    return highlightedChildren === children ? node : cloneElement(node, undefined, ...highlightedChildren);
  }

  const highlightedChildren = highlightNode(children, matcher);
  return highlightedChildren === children ? node : cloneElement(node, undefined, highlightedChildren);
};

const highlightMatches = (node: ReactNode, query = ''): ReactNode => {
  const phrase = query.trim();
  return phrase ? highlightNode(node, new RegExp(`(${escapeRegExp(phrase)})`, 'gi')) : node;
};

export default highlightMatches;
